// UPPDRAG-42 — shared helpers for the new leagues.categories text[] field.
//
// The legacy leagues.category free-form text column stays for now (separate
// UPPDRAG will deprecate it once all consumers move). These helpers
// abstract the dual-source read so callers can just ask for the canonical
// list without caring whether the row has been migrated yet.

export type LeagueCategoryKey = "herr" | "dam" | "mix";

export const LEAGUE_CATEGORY_LABELS: Record<LeagueCategoryKey, string> = {
  herr: "Herr",
  dam: "Dam",
  mix: "Mix",
};

/**
 * Resolve the canonical category list for a league. Prefers the new
 * `categories` text[] column when populated; otherwise derives from the
 * legacy `category` free-form text using the same backfill rule the
 * migration applied (case-insensitive substring matching for
 * herr / dam / mix / öppen / alla).
 *
 * Returns a deduplicated, ordered list (herr, dam, mix). Empty array
 * when neither source matches anything.
 */
export function resolveLeagueCategories(input: {
  categories?: string[] | null;
  category?: string | null;
}): LeagueCategoryKey[] {
  const arr = input.categories;
  if (Array.isArray(arr) && arr.length > 0) {
    const known: LeagueCategoryKey[] = [];
    for (const k of ["herr", "dam", "mix"] as const) {
      if (arr.includes(k)) known.push(k);
    }
    return known;
  }
  const text = (input.category ?? "").toLowerCase();
  const out: LeagueCategoryKey[] = [];
  if (text.includes("herr")) out.push("herr");
  if (text.includes("dam")) out.push("dam");
  if (text.includes("mix") || text.includes("öppen") || text.includes("alla")) out.push("mix");
  return out;
}

/**
 * Human-readable summary of the league's open categories.
 *   ['herr']                → "Herr-serie"
 *   ['mix']                 → "Mix-serie"
 *   ['herr', 'dam']         → "Herr / Dam"
 *   ['herr', 'dam', 'mix']  → "Herr / Dam / Mix"
 *   []                      → fallbackText (defaults to "Öppen")
 */
export function formatLeagueCategoriesLabel(
  keys: LeagueCategoryKey[],
  fallbackText = "Öppen",
): string {
  if (keys.length === 0) return fallbackText;
  if (keys.length === 1) return `${LEAGUE_CATEGORY_LABELS[keys[0]]}-serie`;
  return keys.map((k) => LEAGUE_CATEGORY_LABELS[k]).join(" / ");
}
